import {
  CHANNEL_REPLY_MODE_LABELS,
  ChannelReplyMode,
} from '@21k/shared';
import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  createChannelType,
  getApiErrorMessage,
  getChannelType,
  updateChannelType,
} from '../../api/client';
import type { ChannelType } from '../../api/types';
import { useAuth } from '../../contexts/AuthContext';
import { canEditPlatformConfig, canWritePlatformConfig } from '../../utils/permissions';
import { PLATFORM_CONFIG } from '../platform-config/paths';

const REPLY_MODE_OPTIONS = Object.values(ChannelReplyMode);

export default function ChannelTypeFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [replyMode, setReplyMode] = useState<ChannelReplyMode>(ChannelReplyMode.PARENT_NOTIFY);
  const [requiresIdentifier, setRequiresIdentifier] = useState(false);
  const [requiresCredentials, setRequiresCredentials] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [isSystem, setIsSystem] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canEdit = currentUser && (isNew ? canWritePlatformConfig(currentUser) : canEditPlatformConfig(currentUser));

  useEffect(() => {
    if (isNew || !id) return;
    getChannelType(id)
      .then((t: ChannelType) => {
        setCode(t.code);
        setName(t.name);
        setDescription(t.description ?? '');
        setReplyMode(t.replyMode);
        setRequiresIdentifier(t.requiresIdentifier);
        setRequiresCredentials(t.requiresCredentials);
        setSortOrder(t.sortOrder);
        setIsActive(t.isActive);
        setIsSystem(t.isSystem);
      })
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    setError('');
    try {
      if (isNew) {
        const created = await createChannelType({
          code: code.trim(),
          name: name.trim(),
          description: description.trim() || undefined,
          replyMode,
          requiresIdentifier,
          requiresCredentials,
          sortOrder,
          isActive,
        });
        navigate(PLATFORM_CONFIG.channelType(created.id));
      } else if (id) {
        await updateChannelType(id, {
          name: name.trim(),
          description: description.trim() || null,
          replyMode,
          requiresIdentifier,
          requiresCredentials,
          sortOrder,
          isActive,
        });
        navigate(PLATFORM_CONFIG.channelTypes);
      }
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (!canEdit) {
    return (
      <div className="alert alert-error">
        Only users with Channel Management write/edit permission can manage channel types.{' '}
        <Link to={PLATFORM_CONFIG.channelTypes}>Back to list</Link>
      </div>
    );
  }

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <>
      <div className="page-header">
        <div>
          <Link to={PLATFORM_CONFIG.channelTypes} style={{ fontSize: '0.85rem' }}>
            ← Channel types
          </Link>
          <h1 style={{ marginTop: '0.35rem' }}>
            {isNew ? 'New channel type' : 'Edit channel type'}
          </h1>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="config-form-layout">
        <form className="card card-body" onSubmit={handleSave}>
          <div className="form-section-header">Identity</div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="ct-code">Code</label>
            <input
              id="ct-code"
              className="form-control"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              required
              disabled={!isNew}
              placeholder="e.g. GARAGES"
              pattern="[A-Za-z][A-Za-z0-9_]*"
            />
            <small style={{ color: 'var(--text-muted)' }}>
              Stable identifier — cannot change after create.
            </small>
          </div>
          <div className="form-group">
            <label htmlFor="ct-name">Display name</label>
            <input
              id="ct-name"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g. Garages"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="ct-desc">Description</label>
          <textarea
            id="ct-desc"
            className="form-control"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="form-section-header">Delivery &amp; config</div>

        <div className="form-group">
          <label htmlFor="ct-reply">Reply mode</label>
          <select
            id="ct-reply"
            className="form-control"
            value={replyMode}
            onChange={(e) => setReplyMode(e.target.value as ChannelReplyMode)}
          >
            {REPLY_MODE_OPTIONS.map((mode) => (
              <option key={mode} value={mode}>
                {CHANNEL_REPLY_MODE_LABELS[mode]}
              </option>
            ))}
          </select>
          <small style={{ color: 'var(--text-muted)' }}>
            Controls how agent replies are delivered for tickets on this channel.
          </small>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={requiresIdentifier}
                onChange={(e) => setRequiresIdentifier(e.target.checked)}
              />{' '}
              Requires identifier in channel config
            </label>
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={requiresCredentials}
                onChange={(e) => setRequiresCredentials(e.target.checked)}
              />{' '}
              Requires credentials in channel config
            </label>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="ct-sort">Sort order</label>
          <input
            id="ct-sort"
            type="number"
            min={0}
            className="form-control"
            style={{ maxWidth: 120 }}
            value={sortOrder}
            onChange={(e) => setSortOrder(Number(e.target.value))}
          />
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />{' '}
            Active
          </label>
          {isSystem && (
            <small style={{ display: 'block', color: 'var(--text-muted)' }}>
              Seeded system channel type — you can still rename or deactivate it. Existing tickets keep this channel.
            </small>
          )}
        </div>

        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : isNew ? 'Create channel type' : 'Save changes'}
        </button>
      </form>

        <aside className="card card-body config-help-card">
          <strong>What is a channel type?</strong>
          <p style={{ margin: '0.5rem 0 0' }}>
            Channel types define how tickets arrive (email, widget, WhatsApp, etc.) and how agent
            replies are routed back to parents. Each type has a stable <code>code</code> used in
            APIs and reporting.
          </p>
          <p style={{ margin: '0.75rem 0 0' }}>
            <strong>Reply mode</strong> determines whether replies go via email, WhatsApp, in-app
            notification, or stay internal only.
          </p>
        </aside>
      </div>
    </>
  );
}
